import { CLEAR_BONUS, DIFFICULTIES, LIVES, MAX_PLAYERS, type Difficulty } from "../src/protocol";
import type { Lang } from "../src/languages";
import { LANG } from "./i18n";

// Every visible text lives here, once per language. The `Strings` type is derived from the
// English table, so a key missing in Spanish is a compile error.
//
// Wording: WorkBrawl avoids "game"/"play" vocabulary (some workplace filters block it).
// Say round, room, start, people, title, board.

const size = (d: Difficulty) => `${DIFFICULTIES[d].width}×${DIFFICULTIES[d].height}`;

const en = {
  appName: "MinesBrawl",
  pageTitle: "MinesBrawl — Team minesweeper against the clock",
  tagline: "Team minesweeper against the clock",
  homeIntro: "Open a room, share the link and clear the same board together. Clear as many as you can before time runs out.",
  uiLanguageLabel: "Language",
  backToHub: "Back to WorkBrawl",
  privacy: "Privacy",
  terms: "Terms",
  consentText: "We use analytics cookies to learn how WorkBrawl is used. They\'re off unless you accept.",
  consentAccept: "Accept",
  consentReject: "Reject",
  consentNotice: "We use Google Analytics cookies to understand how WorkBrawl is used and improve it.",
  consentGotIt: "Got it",

  newRoomTitle: "New room",
  nameLabel: "Your name",
  namePlaceholder: "e.g. Alex",
  nameInvalid: "Enter a name of 1 to 20 characters.",
  durationLabel: "Round length",
  minutes: (n: number) => `${n} minutes`,
  difficultyLabel: "Board size",
  difficulties: {
    easy: `Easy · ${size("easy")}, ${DIFFICULTIES.easy.mines} mines`,
    medium: `Medium · ${size("medium")}, ${DIFFICULTIES.medium.mines} mines`,
    hard: `Hard · ${size("hard")}, ${DIFFICULTIES.hard.mines} mines`,
  } satisfies Record<Difficulty, string>,
  difficultyNames: { easy: "Easy", medium: "Medium", hard: "Hard" } satisfies Record<Difficulty, string>,
  createRoom: "Create room",
  creatingRoom: "Creating…",
  verifying: "Checking…",
  or: "or",
  codeLabel: "Room code",
  codePlaceholder: "ABCDE",
  codeInvalid: "Room codes have 5 letters or numbers.",
  join: "Join",
  createFailed: "Could not create the room. Try again.",
  joinFailed: "Could not check the room. Try again.",
  turnstileFailed: "The security check failed. Try again.",
  turnstileUnavailable: "The security check is unavailable. Reload the page and try again.",
  turnstileInteract: "Tick the box below to confirm you're a person.",
  errorRoomNotFound: "This room does not exist or has expired.",
  errorRoomFull: `This room is full (${MAX_PLAYERS} people).`,
  invalidRoomLink: "That room link isn't valid.",

  howTitle: "How it works",
  howSteps: [
    { title: "Open a room.", text: "Pick the round length and the board size, then share the link or the QR code." },
    { title: "Clear it together.", text: "Everyone opens cells on the same board at the same time. The first cell is always safe." },
    { title: "Mind the mines.", text: `Flag the ones you spot. The team can set off ${LIVES} per board; clear the board for ${CLEAR_BONUS} extra points.` },
  ],
  faqTitle: "Frequently asked questions",
  faq: [
    { q: "Is MinesBrawl free?", a: "Yes. It's free, needs no account or download and runs in any modern browser." },
    { q: "How many people fit in a room?", a: `Up to ${MAX_PLAYERS} people per room, each on their own device.` },
    { q: "Do we compete against each other?", a: "No. The whole room is one team against the clock. The scoreboard shows what each person added, just for fun." },
    { q: "How are points counted?", a: `1 point for each safe cell you open, including the ones that open on their own, plus ${CLEAR_BONUS} for the team when a board is cleared. Flags give no points.` },
    { q: "How do I place a flag?", a: "Right-click on a computer, or press and hold on a phone. There is also a flag button that turns every tap into a flag." },
  ],

  namePromptTitle: "What's your name?",
  namePromptBody: "This is how the others will see you in the room.",
  enterRoom: "Enter room",

  room: "Room",
  exit: "Leave",
  cancel: "Cancel",
  exitTitle: "Leave the room?",
  exitBody: "You'll go back to the start screen. Come back with the room link to rejoin.",
  teamScore: "Team score",
  clearedCount: (n: number) => (n === 1 ? "1 cleared" : `${n} cleared`),
  lostCount: (n: number) => (n === 1 ? "1 lost" : `${n} lost`),
  contributions: "Who added what",
  timeLeft: "Time left",
  you: "you",
  offline: "away",
  points: (n: number) => (n === 1 ? "1 point" : `${n} points`),

  inviteTitle: "Invite your team",
  copyLink: "Copy link",
  linkCopied: "Copied!",
  linkCopiedToast: "Link copied",
  copyFailed: "Copy this link:",
  copyFailedShort: "Couldn't copy",
  share: "Share",
  shareText: "Join me on MinesBrawl: team minesweeper against the clock!",
  close: "Close",
  qrLabel: (code: string) => `QR code to join room ${code}`,

  lobbyTitle: "Ready when you are",
  lobbyBody: (n: number) => (n === 1 ? "You're the only one here. Share the link, or start on your own." : `${n} people in the room.`),
  startRound: "Start round",
  getReady: "Get ready",
  boardNumber: (n: number) => `Board ${n}`,
  minesLeft: (n: number) => `${n} mines left`,
  livesLeft: (n: number) => (n === 1 ? "1 life left" : `${n} lives left`),
  flagMode: "Flag",
  flagModeOn: "Flagging: tap a cell",
  flagHint: "Right-click or press and hold to flag",
  clearedBanner: "Board cleared!",
  lostBanner: "Too many mines",
  boardLabel: "Board",
  cellClosed: (x: number, y: number) => `Closed cell, column ${x}, row ${y}`,
  cellFlagged: (x: number, y: number) => `Flagged cell, column ${x}, row ${y}`,
  cellOpen: (x: number, y: number, n: number) => `Open cell, column ${x}, row ${y}: ${n} mines around`,
  cellMine: (x: number, y: number) => `Mine, column ${x}, row ${y}`,
  resultsTitle: "Time's up!",
  resultsScore: (n: number) => (n === 1 ? "Your team scored 1 point" : `Your team scored ${n} points`),
  newRound: "New round",

  connecting: "Connecting…",
  reconnecting: "Connection lost. Reconnecting…",
  backHome: "Back to start",
};

export type Strings = typeof en;

const es: Strings = {
  appName: "MinesBrawl",
  pageTitle: "MinesBrawl — El buscaminas en equipo contra el reloj",
  tagline: "El buscaminas en equipo contra el reloj",
  homeIntro: "Abre una sala, comparte el enlace y despejen el mismo tablero entre todos. Despejen todos los que puedan antes de que se acabe el tiempo.",
  uiLanguageLabel: "Idioma",
  backToHub: "Volver a WorkBrawl",
  privacy: "Privacidad",
  terms: "Términos",
  consentText: "Usamos cookies de analítica para saber cómo se usa WorkBrawl. Están desactivadas salvo que las aceptes.",
  consentAccept: "Aceptar",
  consentReject: "Rechazar",
  consentNotice: "Usamos cookies de Google Analytics para entender cómo se usa WorkBrawl y mejorarlo.",
  consentGotIt: "Entendido",

  newRoomTitle: "Nueva sala",
  nameLabel: "Tu nombre",
  namePlaceholder: "p. ej. Álex",
  nameInvalid: "Escribe un nombre de 1 a 20 caracteres.",
  durationLabel: "Duración de la ronda",
  minutes: (n: number) => `${n} minutos`,
  difficultyLabel: "Tamaño del tablero",
  difficulties: {
    easy: `Fácil · ${size("easy")}, ${DIFFICULTIES.easy.mines} minas`,
    medium: `Medio · ${size("medium")}, ${DIFFICULTIES.medium.mines} minas`,
    hard: `Difícil · ${size("hard")}, ${DIFFICULTIES.hard.mines} minas`,
  },
  difficultyNames: { easy: "Fácil", medium: "Medio", hard: "Difícil" },
  createRoom: "Crear sala",
  creatingRoom: "Creando…",
  verifying: "Verificando…",
  or: "o",
  codeLabel: "Código de sala",
  codePlaceholder: "ABCDE",
  codeInvalid: "Los códigos de sala tienen 5 letras o números.",
  join: "Entrar",
  createFailed: "No se pudo crear la sala. Inténtalo de nuevo.",
  joinFailed: "No se pudo comprobar la sala. Inténtalo de nuevo.",
  turnstileFailed: "La verificación de seguridad falló. Inténtalo de nuevo.",
  turnstileUnavailable: "La verificación de seguridad no está disponible. Recarga la página e inténtalo de nuevo.",
  turnstileInteract: "Marca la casilla de abajo para confirmar que eres una persona.",
  errorRoomNotFound: "Esta sala no existe o ya expiró.",
  errorRoomFull: `Esta sala está llena (${MAX_PLAYERS} personas).`,
  invalidRoomLink: "El enlace de la sala no es válido.",

  howTitle: "Cómo funciona",
  howSteps: [
    { title: "Abre una sala.", text: "Elige la duración de la ronda y el tamaño del tablero, y comparte el enlace o el código QR." },
    { title: "Despéjenlo entre todos.", text: "Todos destapan casillas en el mismo tablero al mismo tiempo. La primera casilla siempre es segura." },
    { title: "Cuidado con las minas.", text: `Marquen con bandera las que detecten. El equipo puede detonar ${LIVES} por tablero; despejarlo completo da ${CLEAR_BONUS} puntos extra.` },
  ],
  faqTitle: "Preguntas frecuentes",
  faq: [
    { q: "¿MinesBrawl es gratis?", a: "Sí. Es gratis, no necesita cuenta ni descarga y funciona en cualquier navegador moderno." },
    { q: "¿Cuántas personas caben en una sala?", a: `Hasta ${MAX_PLAYERS} personas por sala, cada una en su propio dispositivo.` },
    { q: "¿Competimos entre nosotros?", a: "No. Toda la sala es un solo equipo contra el reloj. El marcador muestra lo que sumó cada persona, solo por diversión." },
    { q: "¿Cómo se cuentan los puntos?", a: `1 punto por cada casilla segura que destapas, incluidas las que se abren solas, más ${CLEAR_BONUS} para el equipo al despejar un tablero. Las banderas no dan puntos.` },
    { q: "¿Cómo pongo una bandera?", a: "Con clic derecho en la computadora, o manteniendo pulsado en el teléfono. También hay un botón de bandera que convierte cada toque en una bandera." },
  ],

  namePromptTitle: "¿Cómo te llamas?",
  namePromptBody: "Así te verán los demás en la sala.",
  enterRoom: "Entrar a la sala",

  room: "Sala",
  exit: "Salir",
  cancel: "Cancelar",
  exitTitle: "¿Salir de la sala?",
  exitBody: "Volverás al inicio. Puedes regresar con el enlace de la sala.",
  teamScore: "Puntaje del equipo",
  clearedCount: (n: number) => (n === 1 ? "1 despejado" : `${n} despejados`),
  lostCount: (n: number) => (n === 1 ? "1 perdido" : `${n} perdidos`),
  contributions: "Quién sumó qué",
  timeLeft: "Tiempo restante",
  you: "tú",
  offline: "ausente",
  points: (n: number) => (n === 1 ? "1 punto" : `${n} puntos`),

  inviteTitle: "Invita a tu equipo",
  copyLink: "Copiar enlace",
  linkCopied: "¡Copiado!",
  linkCopiedToast: "Enlace copiado",
  copyFailed: "Copia este enlace:",
  copyFailedShort: "No se pudo copiar",
  share: "Compartir",
  shareText: "¡Únete a MinesBrawl: el buscaminas en equipo contra el reloj!",
  close: "Cerrar",
  qrLabel: (code: string) => `Código QR para entrar a la sala ${code}`,

  lobbyTitle: "Cuando quieran",
  lobbyBody: (n: number) => (n === 1 ? "Por ahora solo estás tú. Comparte el enlace, o empieza por tu cuenta." : `${n} personas en la sala.`),
  startRound: "Empezar ronda",
  getReady: "Prepárate",
  boardNumber: (n: number) => `Tablero ${n}`,
  minesLeft: (n: number) => `Quedan ${n} minas`,
  livesLeft: (n: number) => (n === 1 ? "Queda 1 vida" : `Quedan ${n} vidas`),
  flagMode: "Bandera",
  flagModeOn: "Marcando: toca una casilla",
  flagHint: "Clic derecho o mantén pulsado para marcar",
  clearedBanner: "¡Tablero despejado!",
  lostBanner: "Demasiadas minas",
  boardLabel: "Tablero",
  cellClosed: (x: number, y: number) => `Casilla cerrada, columna ${x}, fila ${y}`,
  cellFlagged: (x: number, y: number) => `Casilla con bandera, columna ${x}, fila ${y}`,
  cellOpen: (x: number, y: number, n: number) => `Casilla abierta, columna ${x}, fila ${y}: ${n} minas alrededor`,
  cellMine: (x: number, y: number) => `Mina, columna ${x}, fila ${y}`,
  resultsTitle: "¡Se acabó el tiempo!",
  resultsScore: (n: number) => (n === 1 ? "Su equipo sumó 1 punto" : `Su equipo sumó ${n} puntos`),
  newRound: "Nueva ronda",

  connecting: "Conectando…",
  reconnecting: "Se perdió la conexión. Reconectando…",
  backHome: "Volver al inicio",
};

const TABLES: Record<Lang, Strings> = { en, es };

export const STRINGS: Strings = TABLES[LANG];

/** Each language written in itself, for the language switcher. */
export const LANGUAGE_NAMES: Record<Lang, string> = { en: "English", es: "Español" };
